import { uiText } from "@/lib/i18n/uiText";
import { seoFor } from "@/lib/i18n/keywordPools";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MainLayout } from "@/components/layout/MainLayout";
import { BLOG_POSTS } from "@/lib/blog";

const TITLE = "Sports Betting Markets Blog — MMA, Boxing, NFL, NBA | Statlign";
const DESC =
  "Statlign's sports blog: how MMA, boxing, NFL, NBA, MLB, NHL and football betting markets are priced, and how our model evaluates each pick.";

const SPORTS = [
  { key: "mma", label: "MMA & UFC", hub: "/mma-predictions" },
  { key: "boxing", label: "Boxing", hub: "/boxing-predictions" },
  { key: "nfl", label: "NFL", hub: "/nfl-predictions" },
  { key: "nba", label: "NBA", hub: "/nba-predictions" },
  { key: "mlb", label: "MLB", hub: "/mlb-predictions" },
  { key: "nhl", label: "NHL", hub: "/nhl-predictions" },
  { key: "football", label: "Football", hub: "/predictions" },
] as const;

function postsFor(key: string) {
  return BLOG_POSTS.filter(
    (p) =>
      p.slug.startsWith(`${key}-`) ||
      (p.tags ?? []).some((t) => t.toLowerCase() === key),
  ).slice(0, 8);
}

export const Route = createFileRoute("/blog/sports")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "keywords", content: seoFor({ topics: ["blog", "nfl", "nba", "ufc"] }).meta },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://statlign.com/blog/sports" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SportsBlog,
});

function SportsBlog() {
  return (
    <MainLayout>
      <div className="space-y-8">
        <header className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold">{uiText("Sports betting markets blog")}</h1>
          <p className="max-w-3xl text-muted-foreground">
             {uiText("Sport-by-sport guides to how bookmakers price each market, what moves a line, and how the Statlign model decides whether a price carries value. Written by the Statlign Sports Desk.")} </p>
        </header>
        {SPORTS.map((s) => {
          const posts = postsFor(s.key);
          return (
            <section key={s.key} className="space-y-3">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-xl font-semibold">{uiText(s.label)}</h2>
                <Link to={s.hub as string} className="text-sm text-primary hover:underline">{uiText(s.label)}  {uiText("predictions")}</Link>
              </div>
              {posts.length === 0 ? (
                <p className="text-sm text-muted-foreground">{uiText("New")} {uiText(s.label)}  {uiText("articles are on the way.")}</p>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {posts.map((p) => (
                    <li key={p.slug} className="rounded-lg border bg-card p-4">
                      <Link to="/blog/$slug" params={{ slug: p.slug }} className="font-medium hover:text-primary">{uiText(p.title)}</Link>
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{uiText(p.description)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </MainLayout>
  );
}
